import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  ClipboardList, Plus, X, CheckCircle2, XCircle, AlertTriangle, ChevronRight, Smartphone, PackageCheck,
} from "lucide-react";
import LoadingState from "@/components/ui/loading-state";
import {
  TabelDesktop, DaftarKartuMobile, KartuBaris, KartuBarisHeader, KartuBarisMeta,
} from "@/components/ui/responsive-table";
import { useSortState, sortRows, SortTh } from "@/components/ui/sort";

const inp = "w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200";

const STATUS = {
  menunggu: { l: "Menunggu", c: "bg-amber-50 text-amber-700 border-amber-200" },
  diproses: { l: "Diproses", c: "bg-sky-50 text-sky-700 border-sky-200" },
  disetujui: { l: "Disetujui", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  ditolak: { l: "Ditolak", c: "bg-rose-50 text-rose-700 border-rose-200" },
};

// Label status: request tetap 'disetujui' walau sebagian ditutup (satu request =
// satu keputusan, brief 4 Sep 2026 P16) — `pemenuhan` yang membedakannya.
function labelStatus(it) {
  const st = STATUS[it.status] || STATUS.menunggu;
  if (it.status === "disetujui" && it.pemenuhan === "sebagian") return { ...st, l: "Disetujui (sebagian)" };
  return st;
}

// Tahap transfer turunan (P17): transfer dari request tidak punya tahap Kirim —
// 'disiapkan' berarti "menunggu diterima di toko tujuan". Dokumen lama tanpa
// `transfer_status` tidak menampilkan apa-apa (perilaku lama).
function teksTahapTransfer(it) {
  if (it.status !== "disetujui" || !it.transfer_status) return null;
  if (it.transfer_dibatalkan_at) return { t: "Transfer dibatalkan", c: "text-rose-600" };
  if (it.transfer_status === "disiapkan" && it.lewati_kirim) return { t: `Menunggu diterima di ${it.warehouse_tujuan_nama}`, c: "text-amber-700" };
  if (it.transfer_status === "disiapkan") return { t: "Disiapkan — menunggu dikirim", c: "text-amber-700" };
  if (it.transfer_status === "dikirim") return { t: "Dikirim — menunggu diterima", c: "text-sky-700" };
  if (it.transfer_status === "diterima") return { t: "Barang sudah diterima", c: "text-emerald-700" };
  if (it.transfer_status === "dibatalkan") return { t: "Transfer dibatalkan", c: "text-rose-600" };
  return null;
}

// Qty yang dipenuhi per baris — dokumen lama (sebelum P16) tidak punya
// `qty_dipenuhi`; fallback ke qty − qty_ditolak, persis makna lama.
const qtyDipenuhi = (it) => (it.qty_dipenuhi ?? (Number(it.qty) - Number(it.qty_ditolak || 0)));

export default function RequestStok() {
  const { can, user } = useAuth();
  const bolehAjukan = can("stock_request", "create");
  // Peninjau = kunci BARU staf gudang (brief 4 Sep 2026 P16) ATAU headstore lama —
  // kunci yang SAMA dengan dependency `_boleh_tinjau_request` di backend.
  const bolehTinjau = can("stock_request_approval", "update") || can("stock_request", "update");
  const bolehLihat = can("stock_request", "view") || can("stock_request_approval", "view")
    || bolehAjukan || bolehTinjau;
  // Gudang tujuan/asal ditarik dari /inventory/warehouses — izinnya inventory_gudang,
  // beda modul dari stock_request. Tanpa penjagaan ini dropdown gudang kosong.
  const bolehGudang = can("inventory_gudang", "view");

  const [gudang, setGudang] = useState([]);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [formAjukan, setFormAjukan] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [g, l] = await Promise.all([
        bolehGudang ? api.get("/inventory/warehouses") : null,
        bolehLihat ? api.get("/inventory/stock-request") : null,
      ]);
      if (g) setGudang(g.data.items || []);
      if (l) setList(l.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat data permintaan stok");
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const kirim = async (fn, pesan) => {
    setBusy(true);
    try {
      const r = await fn();
      toast.success(r?.data?.message || pesan);
      await load();
      return r?.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memproses permintaan");
      return null;
    } finally { setBusy(false); }
  };

  const milikSaya = list.filter(r => r.requested_by === user?.email);
  // Kalau user cuma pengaju (tidak bisa lihat semua), backend sudah menyaring list ke
  // miliknya sendiri — filter di atas jadi no-op dan tetap aman dipakai.
  const daftarTinjau = list;

  if (!bolehLihat) {
    return (
      <div className="p-4 md:p-8" data-testid="request-stok">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
          Akun Anda belum diberi izin melihat Request Stok. Minta admin mencentang modul
          "Request Stok (sales → gudang)" pada role Anda.
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="request-stok">
      <div className="flex items-start justify-between flex-col lg:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Inventory</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Request Stok</h1>
          <p className="text-sm text-slate-600 mt-1">
            Staf mengajukan barang yang dibutuhkan, headstore meninjau lalu menyetujui — persetujuan
            langsung memindahkan barang lewat mesin transfer yang sama.
          </p>
        </div>
        {bolehAjukan && (
          bolehGudang ? (
            <button onClick={() => setFormAjukan({
              warehouse_tujuan_id: gudang[0]?.id || "",
              items: [{ sku: "", nama: "", qty: 1, catatan: "" }],
              keperluan: "",
            })} data-testid="rs-ajukan-buka"
                    className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Ajukan Permintaan
            </button>
          ) : (
            <p className="text-xs text-amber-700">Butuh izin modul Gudang untuk memilih gudang tujuan.</p>
          )
        )}
      </div>

      {loading && <LoadingState loading data-testid="rs-loading" />}

      {!loading && bolehTinjau && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-slate-500" />
            <h2 className="font-display font-bold text-slate-900 text-sm">Semua Pengajuan (Peninjau)</h2>
          </div>
          <TabelRequest items={daftarTinjau} onKlik={setDetail} testidPrefix="rs-tinjau" />
        </div>
      )}

      {!loading && bolehAjukan && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-slate-500" />
            <h2 className="font-display font-bold text-slate-900 text-sm">Pengajuan Saya</h2>
          </div>
          <TabelRequest items={milikSaya} onKlik={setDetail} testidPrefix="rs-saya" />
        </div>
      )}

      {formAjukan && (
        <ModalAjukan form={formAjukan} setForm={setFormAjukan} gudang={gudang} busy={busy}
                     onSimpan={async () => {
                       const body = {
                         warehouse_tujuan_id: formAjukan.warehouse_tujuan_id,
                         keperluan: formAjukan.keperluan,
                         items: formAjukan.items.filter(i => i.sku).map(i => ({
                           sku: i.sku, qty: Number(i.qty) || 0, catatan: i.catatan || "",
                         })),
                       };
                       if (!body.items.length) { toast.error("Tambahkan minimal satu barang"); return; }
                       const r = await kirim(() => api.post("/inventory/stock-request", body), "Permintaan diajukan");
                       if (r) setFormAjukan(null);
                     }} />
      )}

      {detail && (
        <ModalDetail item={detail} onClose={() => setDetail(null)} gudang={gudang}
                     bolehTinjau={bolehTinjau} busy={busy} user={user}
                     onEdit={async (body) => {
                       const r = await kirim(() => api.patch(`/inventory/stock-request/${detail.id}`, body), "Permintaan diperbarui");
                       if (r) setDetail(r.item);
                     }}
                     onApprove={async (body) => {
                       const r = await kirim(() => api.post(`/inventory/stock-request/${detail.id}/approve`, body), "Permintaan disetujui");
                       if (r) setDetail(null);
                     }}
                     onTerima={async () => {
                       // P17: toko tujuan menekan Diterima — memanggil endpoint
                       // delegasi request (mesin transfer yang sama di baliknya).
                       const r = await kirim(() => api.post(`/inventory/stock-request/${detail.id}/terima`, {}), "Barang diterima");
                       if (r) setDetail(null);
                     }}
                     onTolak={async (alasan, itemsTolak) => {
                       // `items` kosong = tolak SELURUH permintaan (perilaku lama).
                       // Diisi = tolak per produk/qty; permintaannya bisa tetap
                       // menunggu dengan sisa yang masih bisa dikirim.
                       const body = itemsTolak?.length ? { alasan, items: itemsTolak } : { alasan };
                       const r = await kirim(
                         () => api.post(`/inventory/stock-request/${detail.id}/tolak`, body),
                         itemsTolak?.length ? "Sebagian ditolak" : "Permintaan ditolak");
                       if (!r) return;
                       // Kalau masih ada sisa, permintaannya belum selesai — muat
                       // ulang detailnya, jangan tutup modalnya.
                       if (r.status === "menunggu" && r.item) setDetail(r.item);
                       else setDetail(null);
                     }} />
      )}
    </div>
  );
}

function TabelRequest({ items, onKlik, testidPrefix }) {
  // Pengurutan (client-side, lihat components/ui/sort.jsx) — urutan bawaan
  // tetap seperti sebelumnya. Backend `/inventory/stock-request` default 300,
  // maks 2000 — sort di sini hanya menata ulang baris yang sudah termuat.
  const sort = useSortState(null, "asc");
  const rows = sortRows(items, sort.sortKey, sort.sortDir, { tanggal: r => r.created_at });
  return (
    <>
      <TabelDesktop>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr className="text-left">
              <SortTh label="Kode / Tanggal" sortKeyName="tanggal" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} testid={`${testidPrefix}-sort-tanggal`} />
              <SortTh label="Tujuan" sortKeyName="warehouse_tujuan_nama" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} testid={`${testidPrefix}-sort-tujuan`} />
              <th className="px-3 py-2.5 font-semibold">Barang</th>
              <th className="px-3 py-2.5 font-semibold">Keperluan</th>
              <SortTh label="Pemohon" sortKeyName="requested_by_name" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} testid={`${testidPrefix}-sort-pemohon`} />
              <SortTh label="Status" sortKeyName="status" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} testid={`${testidPrefix}-sort-status`} />
              <th className="px-3 py-2.5 font-semibold text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Belum ada pengajuan</td></tr>
            ) : rows.map(it => {
              const st = labelStatus(it);
              const tahap = teksTahapTransfer(it);
              return (
                <tr key={it.id} onClick={() => onKlik(it)} data-testid={`${testidPrefix}-${it.id}`}
                    className="hover:bg-slate-50/60 cursor-pointer">
                  <td className="px-3 py-2.5">
                    <p className="font-mono text-[11px] text-slate-500">{it.code}</p>
                    <p className="text-slate-800">{it.created_at?.slice(0, 10)}</p>
                  </td>
                  <td className="px-3 py-2.5 text-slate-700">{it.warehouse_tujuan_nama}</td>
                  <td className="px-3 py-2.5 text-slate-700 text-xs">
                    {(it.items || []).map(x => `${x.sku} x${x.qty}`).join(", ")}
                  </td>
                  <td className="px-3 py-2.5 text-slate-600 text-xs">{it.keperluan || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-600 text-xs">{it.requested_by_name}</td>
                  <td className="px-3 py-2.5">
                    <span className={`text-[11px] px-2 py-0.5 rounded border ${st.c}`}>{st.l}</span>
                    {it.status === "disetujui" && it.transfer_code && (
                      <span className="block text-[10px] text-slate-500 mt-0.5 font-mono">{it.transfer_code}</span>
                    )}
                    {tahap && (
                      <span className={`block text-[10px] mt-0.5 ${tahap.c}`} data-testid={`${testidPrefix}-tahap-${it.id}`}>{tahap.t}</span>
                    )}
                    {it.status === "ditolak" && it.reject_reason && (
                      <span className="block text-[10px] text-rose-600 mt-0.5">{it.reject_reason}</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <ChevronRight className="w-4 h-4 text-slate-400 inline" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TabelDesktop>

      <DaftarKartuMobile testid={`${testidPrefix}-kartu`}>
        {rows.length === 0 ? (
          <p className="px-4 py-10 text-center text-slate-400 text-sm">Belum ada pengajuan</p>
        ) : rows.map(it => {
          const st = labelStatus(it);
          const tahap = teksTahapTransfer(it);
          return (
            <KartuBaris key={it.id} testid={`${testidPrefix}-${it.id}-m`} onClick={() => onKlik(it)}>
              <KartuBarisHeader
                kiri={<>
                  <p className="font-mono text-[11px] text-slate-500">{it.code}</p>
                  <p className="text-sm font-semibold text-slate-900">{it.warehouse_tujuan_nama}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{it.created_at?.slice(0, 10)}</p>
                </>}
                kanan={<>
                  <span className={`text-[11px] px-2 py-0.5 rounded border ${st.c}`}>{st.l}</span>
                  {it.status === "disetujui" && it.transfer_code && (
                    <span className="block text-[10px] text-slate-500 mt-0.5 font-mono">{it.transfer_code}</span>
                  )}
                  {tahap && (
                    <span className={`block text-[10px] mt-0.5 max-w-[140px] text-right ${tahap.c}`}>{tahap.t}</span>
                  )}
                  {it.status === "ditolak" && it.reject_reason && (
                    <span className="block text-[10px] text-rose-600 mt-0.5 max-w-[140px] text-right">{it.reject_reason}</span>
                  )}
                </>}
              />
              <KartuBarisMeta label="Barang" value={(it.items || []).map(x => `${x.sku} x${x.qty}`).join(", ")} />
              <KartuBarisMeta label="Keperluan" value={it.keperluan || "—"} />
              <KartuBarisMeta label="Pemohon" value={it.requested_by_name} />
            </KartuBaris>
          );
        })}
      </DaftarKartuMobile>
    </>
  );
}

function Bingkai({ judul, children, onClose, footer, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid={testid}
           className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">{judul}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-3">{children}</div>
        {footer && <div className="p-4 border-t border-slate-100 flex justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
}

function ModalAjukan({ form, setForm, gudang, busy, onSimpan }) {
  const ubah = (idx, patch) => setForm(f => ({
    ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it),
  }));
  return (
    <Bingkai judul="Ajukan Permintaan Stok" testid="rs-ajukan-modal" onClose={() => setForm(null)}
             footer={
               <>
                 <button onClick={() => setForm(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
                 <button onClick={onSimpan} disabled={busy} data-testid="rs-ajukan"
                         className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                   {busy ? "Mengajukan..." : "Ajukan Permintaan"}
                 </button>
               </>
             }>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Gudang / toko tujuan</label>
        <select value={form.warehouse_tujuan_id} data-testid="rs-tujuan"
                onChange={e => setForm(f => ({ ...f, warehouse_tujuan_id: e.target.value }))} className={inp}>
          <option value="">— pilih gudang tujuan —</option>
          {gudang.map(g => <option key={g.id} value={g.id}>{g.nama}</option>)}
        </select>
      </div>
      <div className="space-y-2">
        <p className="text-xs font-medium text-slate-600">Barang yang dibutuhkan</p>
        {!form.warehouse_tujuan_id && (
          <p className="text-[11px] text-amber-700" data-testid="rs-pilih-tujuan-dulu">
            Pilih gudang tujuan dulu — stok yang ditampilkan adalah stok di gudang yang memasoknya.
          </p>
        )}
        {form.items.map((it, i) => (
          <BarisAutocomplete key={i} item={it} idx={i} onUbah={ubah}
                             warehouseTujuanId={form.warehouse_tujuan_id}
                             bisaHapus={form.items.length > 1}
                             onHapus={(idx) => setForm(f => ({ ...f, items: f.items.filter((_, j) => j !== idx) }))} />
        ))}
        <button onClick={() => setForm(f => ({ ...f, items: [...f.items, { sku: "", nama: "", qty: 1, catatan: "" }] }))}
                data-testid="rs-additem" className="text-sm text-emerald-700 hover:underline">
          + Tambah barang
        </button>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Keperluan</label>
        <input value={form.keperluan} data-testid="rs-keperluan" placeholder="Contoh: stok menipis untuk display toko"
               onChange={e => setForm(f => ({ ...f, keperluan: e.target.value }))} className={inp} />
      </div>
    </Bingkai>
  );
}

function BarisAutocomplete({ item, idx, onUbah, onHapus, bisaHapus, warehouseTujuanId }) {
  const [saran, setSaran] = useState([]);
  // Angka yang ditampilkan adalah stok di GUDANG PEMASOK, bukan stok total
  // (permintaan owner 26 Agu 2026 poin 1). Stok total menyesatkan di layar ini:
  // barang bisa saja banyak, tapi semuanya ada di toko lain dan tidak ada satu
  // pun yang bisa dikirim ke sini.
  const cari = async (teks) => {
    onUbah(idx, { sku: teks.toUpperCase(), nama: teks });
    if (teks.length < 2) return setSaran([]);
    if (!warehouseTujuanId) return setSaran([]);
    try {
      const r = await api.get("/inventory/stock-request/stok-tersedia", {
        params: { warehouse_tujuan_id: warehouseTujuanId, q: teks, limit: 25 },
      });
      setSaran(r.data.items || []);
    } catch { setSaran([]); }
  };
  const pilih = (p) => {
    // Barang yang gudang pemasoknya kosong TIDAK bisa dipilih. Backend juga
    // menolaknya (aturan 2 CLAUDE.md) — ini cuma supaya tidak ada klik yang
    // berujung 400.
    if (!p.bisa_diminta) return;
    onUbah(idx, { sku: p.sku, nama: p.nama, qty_tersedia: p.qty_tersedia });
    setSaran([]);
  };
  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-2 relative">
      <div className="flex items-start gap-2">
        <div className="flex-1 relative">
          <input value={item.nama || item.sku} onChange={e => cari(e.target.value)}
                 placeholder="Ketik SKU atau nama produk" data-testid={`rs-item-cari-${idx}`} className={inp} />
          {item.sku && (
            <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5">
              SKU {item.sku}
            </span>
          )}
          {saran.length > 0 && (
            <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
              {saran.map(p => (
                <button key={p.sku} type="button" onClick={() => pilih(p)} disabled={!p.bisa_diminta}
                        data-testid={`rs-saran-${p.sku}`}
                        title={p.bisa_diminta ? undefined : "Gudang pemasok tidak punya stok barang ini"}
                        className={`w-full text-left px-3 py-2 text-sm border-b border-slate-50 last:border-0 ${
                          p.bisa_diminta ? "hover:bg-slate-50" : "opacity-50 cursor-not-allowed bg-slate-50"}`}>
                  <span className="font-mono text-xs text-slate-500">{p.sku}</span> · {p.nama}
                  {p.has_imei && <span className="ml-1 text-[10px] text-emerald-700">IMEI</span>}
                  <span className={`block text-[11px] ${p.bisa_diminta ? "text-slate-500" : "text-rose-600 font-medium"}`}>
                    {p.bisa_diminta
                      ? `tersedia di gudang: ${p.qty_tersedia}` +
                        (p.per_gudang?.length ? ` (${p.per_gudang.map(g => `${g.nama} ${g.qty}`).join(", ")})` : "")
                      : "stok gudang kosong — tidak bisa diminta"}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
        {bisaHapus && (
          <button type="button" onClick={() => onHapus(idx)} data-testid={`rs-item-del-${idx}`}
                  className="w-10 h-10 rounded hover:bg-rose-50 text-rose-600 grid place-items-center shrink-0">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input type="number" min="1" value={item.qty} placeholder="Qty"
               onChange={e => onUbah(idx, { qty: e.target.value })}
               data-testid={`rs-item-qty-${idx}`} className={inp} />
        <input value={item.catatan} placeholder="Catatan (opsional)"
               onChange={e => onUbah(idx, { catatan: e.target.value })}
               data-testid={`rs-item-catatan-${idx}`} className={inp} />
      </div>
    </div>
  );
}

function ModalDetail({ item, onClose, gudang, bolehTinjau, busy, user, onEdit, onApprove, onTolak, onTerima }) {
  const editable = bolehTinjau && item.status === "menunggu";
  // Approver != pemohon (brief owner 14 Agu poin 6, ditegakkan backend juga —
  // ini cuma mencegah tombol yang ujungnya pasti 403 tampil sama sekali).
  // Superadmin/admin tetap istimewa (jalur darurat cabang dengan satu approver),
  // sama seperti backend `PRIVILEGED_ROLES`.
  const rolePenting = user?.role === "superadmin" || user?.role === "admin";
  const pemohonSendiri = item.requested_by === user?.email;
  const bolehMemutuskan = rolePenting || !pemohonSendiri;
  const [items, setItems] = useState(item.items.map(i => ({ ...i })));
  const [keperluan, setKeperluan] = useState(item.keperluan || "");
  const [catatanRevisi, setCatatanRevisi] = useState("");

  // Gudang ASAL hanya dari gudang PEMASOK tujuan yang dikelola peninjau
  // (`/stock-request/{rid}/gudang-pemasok`, brief 4 Sep 2026 P16) — bukan
  // seluruh daftar gudang: backend menolak asal di luar pemasok, jadi
  // menampilkannya cuma melahirkan tombol yang ujungnya 400.
  const [pemasok, setPemasok] = useState({ items: [], alasan_kosong: null, dimuat: false });
  const [warehouseAsal, setWarehouseAsal] = useState("");
  const [unitPilihan, setUnitPilihan] = useState({});   // sku -> daftar unit tersedia
  const [unitTerpilih, setUnitTerpilih] = useState({}); // sku -> [unit_id,...]
  const [loadingUnit, setLoadingUnit] = useState(false);
  const [alasanTolak, setAlasanTolak] = useState("");
  const [tampilTolak, setTampilTolak] = useState(false);
  // Penolakan per produk / per qty (permintaan owner 26 Agu 2026).
  // `{ [sku]: qty }` — qty 0/kosong berarti tolak seluruh sisa baris itu.
  const [tolakPer, setTolakPer] = useState({});
  // Kuantitas yang DIPENUHI per SKU (P16) — bawaan seluruh sisa (qty − ditolak);
  // sisa yang tidak dipenuhi DITUTUP dalam keputusan yang sama dengan alasan.
  const [dipenuhi, setDipenuhi] = useState(() => Object.fromEntries(
    item.items.map(i => [i.sku, Number(i.qty) - Number(i.qty_ditolak || 0)])));
  const [alasanSisa, setAlasanSisa] = useState("");

  const adaImei = item.items.some(i => i.has_imei);

  useEffect(() => {
    if (!editable) return;
    let batal = false;
    (async () => {
      try {
        const r = await api.get(`/inventory/stock-request/${item.id}/gudang-pemasok`);
        if (batal) return;
        const daftar = r.data.items || [];
        setPemasok({ items: daftar, alasan_kosong: r.data.alasan_kosong || null, dimuat: true });
        if (daftar.length === 1) setWarehouseAsal(daftar[0].id);
      } catch (e) {
        if (batal) return;
        setPemasok({ items: [], alasan_kosong: e?.response?.data?.detail || "Gagal memuat gudang pemasok", dimuat: true });
      }
    })();
    return () => { batal = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id, editable]);

  const muatUnit = async (wid) => {
    if (!wid || !adaImei) return;
    setLoadingUnit(true);
    try {
      const hasil = {};
      for (const it of item.items.filter(i => i.has_imei)) {
        // Pintu milik request (izin peninjau), bukan `/transfer/pilihan-unit`
        // yang butuh `inventory_transfer.view` — staf gudang tidak memilikinya.
        const r = await api.get(`/inventory/stock-request/${item.id}/pilihan-unit`,
          { params: { sku: it.sku, warehouse_id: wid } });
        hasil[it.sku] = r.data.items || [];
      }
      setUnitPilihan(hasil);
      setUnitTerpilih({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat daftar unit ber-IMEI");
    } finally { setLoadingUnit(false); }
  };

  useEffect(() => {
    if (item.status === "menunggu" && warehouseAsal) muatUnit(warehouseAsal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseAsal]);

  const toggleUnit = (sku, unitId) => setUnitTerpilih(u => {
    const cur = u[sku] || [];
    const next = cur.includes(unitId) ? cur.filter(x => x !== unitId) : [...cur, unitId];
    return { ...u, [sku]: next };
  });

  const sisaBaris = (it) => Number(it.qty) - Number(it.qty_ditolak || 0);
  const nDipenuhi = (it) => Math.max(0, Math.min(sisaBaris(it), Number(dipenuhi[it.sku] ?? sisaBaris(it)) || 0));
  const adaSisaDitutup = item.items.some(it => sisaBaris(it) > 0 && nDipenuhi(it) < sisaBaris(it));
  const totalDipenuhi = item.items.reduce((a, it) => a + (sisaBaris(it) > 0 ? nDipenuhi(it) : 0), 0);
  // Unit ber-IMEI harus PERSIS sebanyak yang dipenuhi (bukan qty penuh —
  // bug lama: layar menuntut qty penuh, backend qty sisa, buntu dua arah).
  const siapApprove = !!warehouseAsal && totalDipenuhi > 0
    && (!adaSisaDitutup || !!alasanSisa.trim())
    && item.items.every(it => !it.has_imei || sisaBaris(it) <= 0
      || (unitTerpilih[it.sku] || []).length === nDipenuhi(it));
  const bodyApprove = () => ({
    warehouse_asal_id: warehouseAsal,
    unit_ids: unitTerpilih,
    items: item.items.filter(it => sisaBaris(it) > 0).map(it => ({ sku: it.sku, qty_dipenuhi: nDipenuhi(it) })),
    alasan_sisa: adaSisaDitutup ? alasanSisa.trim() : undefined,
  });
  const bolehTerima = item.status === "disetujui" && item.boleh_terima === true;

  return (
    <Bingkai judul={`Permintaan ${item.code}`} testid="rs-detail-modal" onClose={onClose}
             footer={
               editable ? (
                 bolehMemutuskan ? (
                   <>
                     <button onClick={() => setTampilTolak(v => !v)} data-testid={`rs-tolak-${item.id}`}
                             className="h-10 px-4 rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50 text-sm font-medium inline-flex items-center gap-1.5">
                       <XCircle className="w-4 h-4" /> Tolak
                     </button>
                     {/* Tanpa gudang pemasok yang dikelola, Setujui DISEMBUNYIKAN
                         dengan alasan (bukan tombol mati diam-diam). */}
                     {pemasok.dimuat && pemasok.items.length === 0 ? (
                       <p className="text-xs text-amber-700 max-w-xs" data-testid={`rs-tanpa-pemasok-${item.id}`}>{pemasok.alasan_kosong}</p>
                     ) : (
                       <button onClick={() => onApprove(bodyApprove())}
                               disabled={busy || !siapApprove} data-testid={`rs-approve-${item.id}`}
                               className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
                         <CheckCircle2 className="w-4 h-4" /> Setujui
                       </button>
                     )}
                   </>
                 ) : (
                   <p className="text-xs text-amber-700 flex items-center gap-1.5" data-testid={`rs-pemohon-sendiri-${item.id}`}>
                     <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                     Anda mengajukan permintaan ini sendiri — tidak bisa menyetujui/menolak pengajuan
                     sendiri. Minta headstore/admin lain untuk memprosesnya.
                   </p>
                 )
               ) : bolehTerima ? (
                 <>
                   <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Tutup</button>
                   {/* P17: toko tujuan menekan Diterima — `boleh_terima` dihitung
                       backend (peninjau / pemohon / cabang tujuan), jadi tombol ini
                       tidak pernah tampil untuk yang ujungnya 403. */}
                   <button onClick={onTerima} disabled={busy} data-testid={`rs-terima-${item.id}`}
                           className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
                     <PackageCheck className="w-4 h-4" /> Diterima
                   </button>
                 </>
               ) : (
                 <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Tutup</button>
               )
             }>
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600 space-y-1">
        <p>Tujuan: <strong>{item.warehouse_tujuan_nama}</strong> · Pemohon: <strong>{item.requested_by_name}</strong></p>
        <p>Diajukan: {item.created_at?.slice(0, 16).replace("T", " ")}</p>
      </div>

      {item.status !== "menunggu" && (
        <div className={`border rounded-lg p-3 text-sm ${(STATUS[item.status] || STATUS.menunggu).c}`}>
          Status saat ini: <strong>{(STATUS[item.status] || STATUS.menunggu).l}</strong>
          {item.status === "disetujui" && item.transfer_code && (
            <span> — transfer <span className="font-mono">{item.transfer_code}</span> dari {item.warehouse_asal_nama}</span>
          )}
          {teksTahapTransfer(item) && (
            <p className="mt-1 text-xs font-medium" data-testid={`rs-tahap-${item.id}`}>
              {teksTahapTransfer(item).t}
              {item.transfer_dibatalkan_alasan ? ` — ${item.transfer_dibatalkan_alasan}` : ""}
            </p>
          )}
          {item.status === "ditolak" && item.reject_reason && (
            <span> — alasan: {item.reject_reason}</span>
          )}
          {(item.self_approved_note || item.self_rejected_note) && (
            <p className="mt-1 text-[11px] font-medium flex items-center gap-1" data-testid={`rs-self-note-${item.id}`}>
              <AlertTriangle className="w-3 h-3 shrink-0" /> {item.self_approved_note || item.self_rejected_note}
            </p>
          )}
        </div>
      )}

      <div className="space-y-2">
        <p className="text-xs font-medium text-slate-600">Barang diminta</p>
        {items.map((it, i) => (
          <div key={it.sku} className="border border-slate-200 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-slate-900">
                {it.nama} {it.has_imei && <Smartphone className="w-3 h-3 text-slate-400 inline ml-1" />}
              </p>
              <span className="font-mono text-[11px] text-slate-500">{it.sku}</span>
            </div>
            {editable ? (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <input type="number" min="1" value={it.qty} data-testid={`rs-edit-qty-${it.sku}`}
                         onChange={e => setItems(s => s.map((x, j) => j === i ? { ...x, qty: Number(e.target.value) || 0 } : x))}
                         className={inp} />
                  <input value={it.catatan || ""} placeholder="Catatan" data-testid={`rs-edit-catatan-${it.sku}`}
                         onChange={e => setItems(s => s.map((x, j) => j === i ? { ...x, catatan: e.target.value } : x))}
                         className={inp} />
                </div>
                {Number(it.qty_ditolak || 0) > 0 && (
                  <p className="text-[11px] text-rose-600" data-testid={`rs-ditolak-${it.sku}`}>
                    {it.qty_ditolak} sudah ditolak{it.tolak_alasan ? ` (${it.tolak_alasan})` : ""} — sisa {sisaBaris(it)}
                  </p>
                )}
                {/* Kuantitas yang DIPENUHI (P16). Batas atasnya sisa baris; yang
                    tidak dipenuhi ditutup dengan alasan di bawah — satu keputusan. */}
                {sisaBaris(it) > 0 && (
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <span>Dipenuhi</span>
                    <input type="number" min="0" max={sisaBaris(it)} value={dipenuhi[it.sku] ?? sisaBaris(it)}
                           data-testid={`rs-dipenuhi-${it.sku}`}
                           onChange={e => setDipenuhi(d => ({ ...d, [it.sku]: e.target.value }))}
                           className="w-24 h-8 px-2 border border-slate-200 rounded text-sm" />
                    <span className="text-slate-400">dari {sisaBaris(it)}</span>
                    {nDipenuhi(it) < sisaBaris(it) && (
                      <span className="text-amber-700 font-medium">{sisaBaris(it) - nDipenuhi(it)} ditutup</span>
                    )}
                  </div>
                )}
              </>
            ) : (
              <p className="text-xs text-slate-600">
                Qty {it.qty}{it.catatan ? ` · ${it.catatan}` : ""}
                {Number(it.qty_ditolak || 0) > 0 && (
                  <span className="ml-1 text-rose-600 font-medium" data-testid={`rs-ditolak-${it.sku}`}>
                    · {it.qty_ditolak} ditolak
                    {it.tolak_alasan ? ` (${it.tolak_alasan})` : ""}
                  </span>
                )}
                {item.status === "disetujui" && (
                  <span className="ml-1 font-medium text-emerald-700" data-testid={`rs-dipenuhi-info-${it.sku}`}>
                    · dipenuhi {qtyDipenuhi(it)}
                    {Number(it.qty_ditutup || 0) > 0 && (
                      <span className="text-amber-700"> · {it.qty_ditutup} ditutup{it.alasan_sisa ? ` (${it.alasan_sisa})` : ""}</span>
                    )}
                  </span>
                )}
              </p>
            )}

            {editable && it.has_imei && sisaBaris(it) > 0 && (
              <div className="pt-1">
                <p className="text-[11px] text-slate-500 mb-1">
                  Pilih {nDipenuhi(it)} unit dari gudang asal
                  {" "}({(unitTerpilih[it.sku] || []).length}/{nDipenuhi(it)} terpilih)
                </p>
                {!warehouseAsal ? (
                  <p className="text-[11px] text-amber-700">Pilih gudang asal dulu di bawah.</p>
                ) : loadingUnit ? (
                  <LoadingState loading jumlahKartu={1} data-testid="rs-unit-loading" />
                ) : (unitPilihan[it.sku] || []).length === 0 ? (
                  <p className="text-[11px] text-rose-600">Tidak ada unit tersedia di gudang asal untuk SKU ini.</p>
                ) : (
                  <div className="border border-slate-200 rounded-lg max-h-40 overflow-y-auto divide-y divide-slate-100">
                    {unitPilihan[it.sku].map(u => (
                      <label key={u.id} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50">
                        <input type="checkbox" checked={(unitTerpilih[it.sku] || []).includes(u.id)}
                               onChange={() => toggleUnit(it.sku, u.id)}
                               data-testid={`rs-unit-${u.identifier}`} className="w-4 h-4 rounded border-slate-300" />
                        <span className="font-mono text-xs">{u.identifier}</span>
                        <span className="text-[11px] text-slate-500">masuk {u.tanggal_masuk}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {editable && (
        <>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Keperluan</label>
            <input value={keperluan} onChange={e => setKeperluan(e.target.value)}
                   data-testid="rs-edit-keperluan" className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Catatan revisi (kalau mengubah)</label>
            <input value={catatanRevisi} onChange={e => setCatatanRevisi(e.target.value)}
                   placeholder="Kenapa qty/catatan diubah — opsional" data-testid="rs-catatan-revisi" className={inp} />
          </div>
          <button onClick={() => onEdit({
                    items: items.map(({ sku, qty, catatan }) => ({ sku, qty: Number(qty) || 0, catatan: catatan || "" })),
                    keperluan, catatan_revisi: catatanRevisi,
                  })} disabled={busy} data-testid="rs-simpan-edit"
                  className="h-9 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium">
            Simpan Perubahan
          </button>

          <div className="border-t border-slate-100 pt-3">
            <label className="block text-xs font-medium text-slate-600 mb-1">Gudang asal / pemasok (untuk menyetujui)</label>
            {pemasok.dimuat && pemasok.items.length === 0 ? (
              <p className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3 shrink-0" /> {pemasok.alasan_kosong}</p>
            ) : (
              <select value={warehouseAsal} onChange={e => setWarehouseAsal(e.target.value)}
                      data-testid="rs-gudang-asal" className={inp}>
                <option value="">— pilih gudang pemasok —</option>
                {pemasok.items.map(g => <option key={g.id} value={g.id}>{g.nama}</option>)}
              </select>
            )}
          </div>

          {adaSisaDitutup && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <label className="block text-xs font-medium text-amber-800 mb-1">
                Alasan sisa yang tidak dipenuhi (wajib) — sisa DITUTUP, tidak menunggu lagi
              </label>
              <input value={alasanSisa} onChange={e => setAlasanSisa(e.target.value)}
                     placeholder="Contoh: stok gudang hanya cukup sebagian" data-testid="rs-alasan-sisa" className={inp} />
            </div>
          )}

          {tampilTolak && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 space-y-2">
              <label className="block text-xs font-medium text-rose-800">Alasan penolakan (wajib)</label>
              <textarea value={alasanTolak} onChange={e => setAlasanTolak(e.target.value)}
                        data-testid="rs-alasan-tolak" rows={2}
                        className="w-full px-3 py-2 border border-rose-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-200" />

              {/* Penolakan PER PRODUK / PER QTY (permintaan owner 26 Agu 2026).
                  Tanpa memilih apa pun, tombol menolak SELURUH permintaan —
                  perilaku lama. Begitu ada yang dicentang, yang ditolak hanya
                  baris itu dan sisanya tetap menunggu, jadi staf tidak perlu
                  mengajukan ulang dari nol. */}
              {item.items.length > 0 && (
                <div className="border border-rose-200 rounded-lg bg-white divide-y divide-rose-50"
                     data-testid="rs-tolak-per-item">
                  <p className="px-3 py-1.5 text-[11px] text-rose-800 font-medium">
                    Tolak sebagian? Centang barangnya (kosongkan semua = tolak seluruh permintaan)
                  </p>
                  {item.items.map(it => {
                    const sisa = Number(it.qty) - Number(it.qty_ditolak || 0);
                    const dipilih = Object.prototype.hasOwnProperty.call(tolakPer, it.sku);
                    if (sisa <= 0) return (
                      <p key={it.sku} className="px-3 py-2 text-[11px] text-slate-400">
                        {it.sku} — sudah ditolak seluruhnya
                      </p>
                    );
                    return (
                      <div key={it.sku} className="px-3 py-2 flex items-center gap-2">
                        <input type="checkbox" checked={dipilih} data-testid={`rs-tolak-cek-${it.sku}`}
                               onChange={e => setTolakPer(t => {
                                 const n = { ...t };
                                 if (e.target.checked) n[it.sku] = ""; else delete n[it.sku];
                                 return n;
                               })}
                               className="w-4 h-4 rounded border-rose-300" />
                        <span className="flex-1 text-xs">
                          <span className="font-mono text-slate-500">{it.sku}</span> {it.nama}
                          <span className="text-slate-400"> · sisa {sisa}</span>
                        </span>
                        {dipilih && (
                          <input type="number" min="1" max={sisa} value={tolakPer[it.sku]}
                                 placeholder={`semua (${sisa})`} data-testid={`rs-tolak-qty-${it.sku}`}
                                 onChange={e => setTolakPer(t => ({ ...t, [it.sku]: e.target.value }))}
                                 className="w-24 h-8 px-2 border border-rose-200 rounded text-xs" />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <button onClick={() => {
                        if (!alasanTolak.trim()) { toast.error("Alasan penolakan wajib diisi"); return; }
                        const dipilih = Object.entries(tolakPer).map(([sku, q]) => ({
                          sku, qty_ditolak: q === "" ? null : Number(q) || null,
                        }));
                        onTolak(alasanTolak.trim(), dipilih);
                        setTolakPer({});
                      }} disabled={busy} data-testid="rs-kirim-tolak"
                      className="h-9 px-3 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium disabled:opacity-50">
                {Object.keys(tolakPer).length ? "Tolak Barang Terpilih" : "Tolak Seluruh Permintaan"}
              </button>
            </div>
          )}

          {!adaImei && !warehouseAsal && (
            <p className="text-[11px] text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Pilih gudang asal sebelum menyetujui.</p>
          )}
        </>
      )}

      {(item.edit_log || []).length > 0 && (
        <div className="border-t border-slate-100 pt-3">
          <p className="text-xs font-semibold text-slate-600 mb-1.5">Riwayat Perubahan</p>
          <div className="space-y-2">
            {item.edit_log.map((e, i) => (
              <div key={i} className="text-[11px] text-slate-500 border-l-2 border-slate-200 pl-2">
                <p>{e.at?.slice(0, 16).replace("T", " ")} · {e.by_name}{e.catatan_revisi ? ` — ${e.catatan_revisi}` : ""}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </Bingkai>
  );
}
